import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiService } from '../../../../services/api';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useToast } from '../../../../contexts/ToastContext';
import type { AbsenceTypeValue, StaffAbsencePayload, AbsenceApptConflictDto, StaffAbsenceDto, AbsencePolicyBreachDto } from '../../../../types/api-responses';
import { resolveWorkingWindow, isOutsideWorkingHours, findOverlappingAbsence, describeAbsence } from '../../../../lib/staffScheduleHint';
import AbsenceConflictConfirm from '../../../../components/StaffAbsence/AbsenceConflictConfirm';
import s from './QuickAbsenceModal.module.css';

/** "HH:mm" → minutes since midnight. */
const toMin = (t: string): number => {
    const [h, m] = (t ?? '').split(':').map(Number);
    return (Number.isNaN(h) ? 0 : h) * 60 + (Number.isNaN(m) ? 0 : m);
};

/**
 * AB#1976 (CR-2026-07-04) — Quick "add break / time off" editor launched from
 * the calendar day view's staff dropdown. Prefilled with the staff member and
 * the day currently shown on the calendar, so the owner can log a break for
 * "this staff, this day" in two clicks — the Fresha-style flow from the
 * original request. Same backend + validation as the ManageStaffModal section.
 */
export interface QuickAbsenceModalProps {
    staffId: number;
    staffName: string;
    branchId: number;
    /** The calendar's active day (yyyy-MM-dd). Prefills the absence date. */
    date: string;
    /**
     * AB#2021 — when set, the modal opens in EDIT mode: fields are prefilled
     * from this absence and Save does a PUT (upsert by id) instead of a create.
     * Used by the availability panel's "edit break" quick action.
     */
    editAbsence?: StaffAbsenceDto;
    onClose: () => void;
    onSaved: () => void;
}

const INTRA_DAY: AbsenceTypeValue[] = [0, 3, 4]; // Break, Personal time, Emergency (time window)
const REASON_TYPES: AbsenceTypeValue[] = [0, 4]; // Reason only meaningful for Break / Emergency

// BE serialises absenceType as the enum NAME; map back to the numeric value for edit prefill.
const TYPE_NAME_TO_VALUE: Record<string, AbsenceTypeValue> = {
    Break: 0, SickLeave: 1, Vacation: 2, PersonalTime: 3, Emergency: 4,
};

const TYPE_OPTIONS: { value: AbsenceTypeValue; key: string }[] = [
    { value: 0, key: 'staff.absence.type.break' },
    { value: 1, key: 'staff.absence.type.sick' },
    { value: 2, key: 'staff.absence.type.vacation' },
    { value: 3, key: 'staff.absence.type.personal' },
    { value: 4, key: 'staff.absence.type.emergency' },
];

const REASON_OPTIONS = [
    { value: 'Lunch', key: 'staff.absence.reason.lunch' },
    { value: 'Prayer', key: 'staff.absence.reason.prayer' },
    { value: 'Rest', key: 'staff.absence.reason.rest' },
    { value: 'Medical', key: 'staff.absence.reason.medical' },
    { value: 'Other', key: 'staff.absence.reason.other' },
];

const QuickAbsenceModal: React.FC<QuickAbsenceModalProps> = ({
    staffId, staffName, branchId, date, editAbsence, onClose, onSaved,
}) => {
    const { t, language } = useLanguage();
    const toast = useToast();

    const isEdit = editAbsence != null;
    const [type, setType] = useState<AbsenceTypeValue>(
        editAbsence ? (TYPE_NAME_TO_VALUE[editAbsence.absenceType] ?? 0) : 0);
    const [startDate, setStartDate] = useState(editAbsence?.startDate ?? date);
    const [endDate, setEndDate] = useState(editAbsence?.endDate ?? date);
    const [startTime, setStartTime] = useState(editAbsence?.startTime?.slice(0, 5) ?? '13:00');
    const [endTime, setEndTime] = useState(editAbsence?.endTime?.slice(0, 5) ?? '14:00');
    const [reason, setReason] = useState(editAbsence?.reason ?? 'Lunch');
    const [isRecurring, setIsRecurring] = useState(editAbsence?.isRecurring ?? false);

    const isIntraDay = INTRA_DAY.includes(type);
    const showReason = REASON_TYPES.includes(type);

    // AB#2016 — appointment-conflict confirm (non-null → dialog shown).
    const [conflicts, setConflicts] = useState<AbsenceApptConflictDto[] | null>(null);
    // AB#2024 — break-cap override (set alongside conflicts when the dialog shows).
    const [policyBreach, setPolicyBreach] = useState<AbsencePolicyBreachDto | null>(null);

    const isBreak = type === 0;

    // AB#2024 — the branch break policy (caps), for the allowance hint + the
    // single-break hard block. Server is authoritative; this is the live UX.
    const { data: policy } = useQuery({
        queryKey: ['branch-absence-policy', branchId] as const,
        queryFn: () => apiService.getBranchAbsencePolicy(branchId),
        staleTime: 300_000,
    });

    // AB#2011 — the staff's per-branch schedule, to show working hours for the
    // chosen day and softly warn when an intra-day break falls outside them.
    const { data: schedule } = useQuery({
        queryKey: ['staff-schedule', staffId] as const,
        queryFn: () => apiService.getStaffSchedule(staffId),
        staleTime: 60_000,
    });
    const workWindow = useMemo(
        () => resolveWorkingWindow(schedule?.slots, branchId, startDate),
        [schedule, branchId, startDate],
    );
    const outOfHours = isIntraDay && isOutsideWorkingHours(workWindow, startTime, endTime);

    // AB#2012 — existing absences for this staff, to block overlaps client-side.
    const { data: absencesData } = useQuery({
        queryKey: ['staff-absences', staffId] as const,
        queryFn: () => apiService.getStaffAbsences(staffId),
        staleTime: 30_000,
    });

    // AB#2024 — break allowance: this break's length, and how much break time
    // the staff already has on this day (excluding the row being edited).
    const breakMinutes = isBreak && isIntraDay ? Math.max(0, toMin(endTime) - toMin(startTime)) : 0;
    const usedToday = useMemo(() => {
        const rows = (absencesData?.absences ?? []).filter(a =>
            a.absenceType === 'Break' && a.startDate === startDate
            && a.id !== (editAbsence?.id ?? -1) && a.startTime && a.endTime);
        return {
            count: rows.length,
            minutes: rows.reduce((sum, a) => sum + Math.max(0, toMin(a.endTime!.slice(0, 5)) - toMin(a.startTime!.slice(0, 5))), 0),
        };
    }, [absencesData, startDate, editAbsence]);
    // Single-break length hard block (mirrors the server; no override).
    const overSingleBreak = isBreak && policy != null && breakMinutes > policy.maxSingleBreakMinutes;

    const createMut = useMutation({
        mutationFn: (payload: StaffAbsencePayload) => isEdit
            ? apiService.updateStaffAbsence(staffId, editAbsence!.id, payload)
            : apiService.createStaffAbsence(staffId, payload),
        onSuccess: (res) => {
            // AB#2016 + AB#2024 — the staff has live bookings in this window
            // and/or the break exceeds the branch caps; nothing saved. Show the
            // confirm listing whichever reasons apply.
            if (res.requiresConfirmation) {
                setConflicts(res.conflictingAppointments ?? []);
                setPolicyBreach(res.policyBreach ?? null);
                return;
            }
            setConflicts(null);
            setPolicyBreach(null);
            toast.showToast(t('staff.absence.toast.saved'), 'success');
            for (const w of res.warnings ?? []) {
                toast.showToast(language === 'ar' ? w.messageAr : w.message, 'warning');
            }
            onSaved();
        },
        onError: (err: unknown) => {
            // AB#2012 — surface the server block reason (localized). The BE
            // returns an "Overlap" key or a working-hours message on 400.
            const raw = JSON.stringify(
                (err as { response?: { data?: unknown } })?.response?.data ?? '');
            const key = raw.includes('Overlap') ? 'staff.absence.error.overlap'
                : (raw.includes('working hours') || raw.includes('not scheduled'))
                    ? 'staff.absence.error.outsideHours'
                    : 'staff.absence.toast.saveFailed';
            toast.showToast(t(key), 'error');
        },
    });

    const submit = () => {
        // Intra-day types have a single Date field (endDate is derived from
        // startDate in the payload below), so the range check must not run —
        // otherwise moving the date forward trips a false "end < start" error.
        if (!isIntraDay && endDate < startDate) {
            toast.showToast(t('staff.absence.error.dateOrder'), 'error');
            return;
        }
        if (isIntraDay && endTime <= startTime) {
            toast.showToast(t('staff.absence.error.timeOrder'), 'error');
            return;
        }
        // AB#2012 — hard block (mirrors the server): out-of-hours/off-day for
        // intra-day types, and overlapping/duplicate absences. The BE is the
        // source of truth; this gives an immediate localized block.
        if (outOfHours) {
            toast.showToast(t('staff.absence.error.outsideHours'), 'error');
            return;
        }
        // AB#2024 — a break longer than the branch's single-break limit is a
        // hard block (the inline error + "switch to Personal leave" shows).
        if (overSingleBreak) return;
        const effEndDate = isIntraDay ? startDate : endDate;
        const conflict = findOverlappingAbsence(
            absencesData?.absences, editAbsence?.id ?? null, startDate, effEndDate,
            isIntraDay ? startTime : null, isIntraDay ? endTime : null);
        if (conflict) {
            // AB#2018 — a whole-day-vs-whole-day clash reads as "already on
            // leave for these dates"; an intra-day clash is a duplicate window.
            const key = isIntraDay ? 'staff.absence.error.overlapDetail' : 'staff.absence.error.leaveOverlap';
            toast.showToast(
                t(key).replace('{details}', describeAbsence(conflict, t)),
                'error');
            return;
        }
        createMut.mutate(buildPayload(false));
    };

    const buildPayload = (force: boolean): StaffAbsencePayload => ({
        absenceType: type,
        branchId,
        startDate,
        endDate: isIntraDay ? startDate : endDate,
        startTime: isIntraDay ? `${startTime}:00` : null,
        endTime: isIntraDay ? `${endTime}:00` : null,
        reason: showReason ? reason : null,
        isRecurring: type === 0 ? isRecurring : false,
        recurrencePattern: type === 0 && isRecurring ? 'Weekdays' : null,
        force,
    });

    return (
        <div className={s.overlay} onMouseDown={onClose}>
            <div className={s.card} onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                <div className={s.header}>
                    <div>
                        <h2 className={s.title}>{t(isEdit ? 'staff.absence.editBreak' : 'staff.absence.add')}</h2>
                        <p className={s.subtitle}>{staffName}</p>
                    </div>
                    <button type="button" className={s.close} aria-label={t('staff.absence.cancel')} onClick={onClose}>×</button>
                </div>

                <div className={s.body}>
                    <label className={s.field}>
                        <span>{t('staff.absence.field.type')}</span>
                        <select value={type} onChange={(e) => setType(Number(e.target.value) as AbsenceTypeValue)}>
                            {TYPE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{t(o.key)}</option>
                            ))}
                        </select>
                    </label>

                    {isIntraDay ? (
                        <div className={s.row}>
                            <label className={s.field}>
                                <span>{t('staff.absence.field.date')}</span>
                                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                            </label>
                            <label className={s.field}>
                                <span>{t('staff.absence.field.startTime')}</span>
                                <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
                            </label>
                            <label className={s.field}>
                                <span>{t('staff.absence.field.endTime')}</span>
                                <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
                            </label>
                        </div>
                    ) : (
                        <div className={s.row}>
                            <label className={s.field}>
                                <span>{t('staff.absence.field.startDate')}</span>
                                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                            </label>
                            <label className={s.field}>
                                <span>{t('staff.absence.field.endDate')}</span>
                                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                            </label>
                        </div>
                    )}

                    {isIntraDay && workWindow.kind !== 'unknown' && (
                        <div className={s.hoursHint}>
                            {workWindow.kind === 'working'
                                ? t('staff.absence.hours.works').replace(
                                    '{range}', `⁨${workWindow.start}–${workWindow.end}⁩`)
                                : t('staff.absence.hours.off')}
                        </div>
                    )}
                    {outOfHours && (
                        <div className={s.hoursWarn}>⚠ {t('staff.absence.hours.warnOutside')}</div>
                    )}

                    {/* AB#2024 — live break allowance for this staff + day. */}
                    {isBreak && isIntraDay && policy != null && (
                        <div className={s.hoursHint}>
                            {t('staff.absence.break.allowance')
                                .replace('{used}', String(usedToday.minutes))
                                .replace('{total}', String(policy.maxBreakMinutesPerDay))
                                .replace('{n}', String(usedToday.count))
                                .replace('{max}', String(policy.maxBreaksPerDay))
                                .replace('{this}', String(breakMinutes))}
                        </div>
                    )}
                    {/* AB#2024 — single-break hard block: too long → Personal leave. */}
                    {overSingleBreak && (
                        <div className={s.hoursWarn}>
                            {t('staff.absence.break.tooLong').replace('{max}', String(policy!.maxSingleBreakMinutes))}
                            <button
                                type="button"
                                className={s.convertBtn}
                                onClick={() => setType(3 /* PersonalTime — uncapped */)}
                            >
                                {t('staff.absence.break.switchToPersonal')}
                            </button>
                        </div>
                    )}

                    {showReason && (
                        <div className={s.row}>
                            <label className={s.field}>
                                <span>{t('staff.absence.field.reason')}</span>
                                <select value={reason} onChange={(e) => setReason(e.target.value)}>
                                    {REASON_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{t(o.key)}</option>
                                    ))}
                                </select>
                            </label>
                            {type === 0 && (
                                <label className={`${s.field} ${s.checkField}`}>
                                    <input type="checkbox" checked={isRecurring} onChange={(e) => setIsRecurring(e.target.checked)} />
                                    <span>{t('staff.absence.field.recurring')}</span>
                                </label>
                            )}
                        </div>
                    )}
                </div>

                <div className={s.footer}>
                    <button type="button" className={s.saveBtn} disabled={createMut.isPending || overSingleBreak} onClick={submit}>
                        {t('staff.absence.save')}
                    </button>
                    <button type="button" className={s.cancelBtn} onClick={onClose}>
                        {t('staff.absence.cancel')}
                    </button>
                </div>
            </div>

            {(conflicts || policyBreach) && (
                <AbsenceConflictConfirm
                    conflicts={conflicts ?? []}
                    policyBreach={policyBreach}
                    saving={createMut.isPending}
                    onConfirm={() => createMut.mutate(buildPayload(true))}
                    onCancel={() => { setConflicts(null); setPolicyBreach(null); }}
                />
            )}
        </div>
    );
};

export default QuickAbsenceModal;
